import type { RemovableRef} from '@vueuse/core';
import { StorageSerializers, useLocalStorage } from '@vueuse/core';
import type { ColumnState } from 'ag-grid-community';
import { defineStore } from 'pinia';

type GridState = {
    id: string,
    filterModel: { [key: string]: any; } | null;
    columnState: ColumnState[] | null;
    columnGroupState: { groupId: string; open: boolean; }[] | null;
};

type GridSearch = {
    id: string;
    value: string;
};

type GridScroll = {
    id: string;
    value: number;
};

type GridGroup = {
    id: string;
    values: string[];
};

type GridSelection = {
    id: string;
    values: string[];
};

type StoreState = {
    _gridStates: RemovableRef<GridState[]>;
    _gridSearch: GridSearch[];
    _gridScroll: GridScroll[];
    _gridGroup: GridGroup[];
    _gridSelection: GridSelection[];
};

export const useGridStore = defineStore('gridState', {
    state: (): StoreState => ({
        _gridStates: useLocalStorage<GridState[]>('gridStates', [], { serializer: StorageSerializers.object }),
        _gridSearch: [],
        _gridScroll: [],
        _gridGroup: [],
        _gridSelection: []
    }),
    getters: {
        gridState: state => (id: string): GridState | null => {
            const result = state._gridStates.find(x => x.id === id);
            return result ?? null;
        },
        gridSearch: state => (id: string): string => {
            const result = state._gridSearch.find(x => x.id === id);
            return result?.value ?? '';
        },
        gridScroll: state => (id: string): number => {
            const result = state._gridScroll.find(x => x.id === id);
            return result?.value ?? 0;
        },
        gridGroup: state => (id: string): string[] => {
            const result = state._gridGroup.find(x => x.id === id);
            return result?.values ?? [];
        },
        gridSelection: state => (id: string): string[] => {
            const result = state._gridSelection.find(x => x.id === id);
            return result?.values ?? [];
        }
    },
    actions: {
        setGridState(state: GridState) {
            const index = this._gridStates.findIndex(x => x.id === state.id);
            if (index === -1) {
                this._gridStates.push(state);
            } else {
                this._gridStates[index] = state;
            }
        },
        setGridSearch(id: string, searchValue: string) {
            const index = this._gridSearch.findIndex(x => x.id === id);
            if (index === -1) {
                this._gridSearch.push({
                    id,
                    value: searchValue
                });
            } else {
                this._gridSearch[index].value = searchValue;
            }
        },
        /** Clears what a user typed or filtered on, keeps column state. */
        clearSearchAndFilters() {
            this._gridSearch = [];
            this._gridStates = this._gridStates.map(state => ({ ...state, filterModel: null }));
        },
        setGridScroll(id: string, scrollValue: number) {
            const index = this._gridScroll.findIndex(x => x.id === id);
            if (index === -1) {
                this._gridScroll.push({
                    id,
                    value: scrollValue
                });
            } else {
                this._gridScroll[index].value = scrollValue;
            }
        },
        setGridGroup(id: string, groupValue: string, active: boolean) {
            const group = this._gridGroup.find(x => x.id === id);

            if (group != null) {
                if (active) {
                    group.values.push(groupValue);
                } else {
                    const groupIndex = group.values.findIndex(x => x === groupValue);
                    if (groupIndex !== -1) {
                        group.values.splice(groupIndex, 1);
                    }
                }
            } else if (active) {
                this._gridGroup.push({
                    id,
                    values: [groupValue]
                });
            }
        },
        setGridGroups(id: string, groupValues: string[]) {
            const group = this._gridGroup.find(x => x.id === id);

            if (group != null) {
                group.values = groupValues;
            } else {
                this._gridGroup.push({
                    id,
                    values: groupValues
                });
            }
        },
        setGridSelection(id: string, selectionValue: string, active: boolean) {
            const selection = this._gridSelection.find(x => x.id === id);

            if (selection != null) {
                if (active) {
                    selection.values.push(selectionValue);
                } else {
                    const selectionIndex = selection.values.findIndex(x => x === selectionValue);
                    if (selectionIndex !== -1) {
                        selection.values.splice(selectionIndex, 1);
                    }
                }
            } else if (active) {
                this._gridSelection.push({
                    id,
                    values: [selectionValue]
                });
            }
        },
        setGridSelections(id: string, selectionValues: string[]) {
            const selection = this._gridSelection.find(x => x.id === id);

            if (selection != null) {
                selection.values = selectionValues;
            } else {
                this._gridSelection.push({
                    id,
                    values: selectionValues
                });
            }
        },
        resetGridState(id: string) {
            const index = this._gridStates.findIndex(x => x.id === id);
            if (index !== -1) {
                this._gridStates.splice(index, 1);
            }

            const searchIndex = this._gridSearch.findIndex(x => x.id === id);
            if (searchIndex !== -1) {
                this._gridSearch.splice(searchIndex, 1);
            }

            const scrollIndex = this._gridScroll.findIndex(x => x.id === id);
            if (scrollIndex !== -1) {
                this._gridScroll.splice(scrollIndex, 1);
            }

            const groupIndex = this._gridGroup.findIndex(x => x.id === id);
            if (groupIndex !== -1) {
                this._gridGroup.splice(groupIndex, 1);
            }
        }
    }
});
